import { SettingsSection } from "components/Settings/SettingsSection";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";

import Paper from "@mui/material/Paper";
import { useFetchGetV2 } from "hooks/fetch/useFetchGetV2";
import getFrontEndVersion from "components/utils";
import { siteSettings } from "core/siteSettings";
import { EmailScheduleRunTable } from "components/SysInfo/EmailScheduleRunTable";
import { useLoadCounter } from "hooks/useLoadCounter";

export const SysInfoSection = () => {
  const { loading: sysInfoLoading, response: sysInfoResponse } = useFetchGetV2(
    `settings/sysinfo`
  );
  const tableLoadCount = useLoadCounter(sysInfoLoading);

  if (sysInfoLoading) {
    return <></>;
  }

  const sysInfo = sysInfoResponse;

  const frontEndVersion = getFrontEndVersion(true);

  return (
    <>
      <SettingsSection title="Front end configuration">
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-col={1}>Name</TableCell>
                <TableCell data-col={2}>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              data-qa_loading={sysInfoLoading}
              data-qa_load_count={tableLoadCount}
            >
              <TableRow>
                <TableCell data-col={1}>Front end version</TableCell>
                <TableCell data-col={2}>{frontEndVersion}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Server url</TableCell>
                <TableCell data-col={2}>{siteSettings.siteApiURL}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Cognito Region</TableCell>
                <TableCell data-col={2}>
                  {siteSettings.cognito.region}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Cognito User Pool ID</TableCell>
                <TableCell data-col={2}>
                  {siteSettings.cognito.userPoolId}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>
                  Cognito User Pool Web Client ID
                </TableCell>
                <TableCell data-col={2}>
                  {siteSettings.cognito.userPoolWebClientId}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Cognito Domain</TableCell>
                <TableCell data-col={2}>
                  {siteSettings.cognito.domain}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Cognito Redirect Sign In</TableCell>
                <TableCell data-col={2}>
                  {siteSettings.cognito.redirectSignIn}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Cognito Redirect Sign Out</TableCell>
                <TableCell data-col={2}>
                  {siteSettings.cognito.redirectSignOut}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </SettingsSection>

      <SettingsSection title="Backend sys info">
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-col={1}>Name</TableCell>
                <TableCell data-col={2}>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              data-qa_loading={sysInfoLoading}
              data-qa_load_count={tableLoadCount}
            >
              <TableRow>
                <TableCell data-col={1}>Backend version</TableCell>
                <TableCell data-col={2}>{sysInfo.backend_version}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Python version</TableCell>
                <TableCell data-col={2}>{sysInfo.python_version}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>OAuth certificate url</TableCell>
                <TableCell data-col={2}>{sysInfo.oauth_cert_url}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>OAuth user information url</TableCell>
                <TableCell data-col={2}>
                  {sysInfo.oauth_user_info_url}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>
                  OAuth to Cognito automatically migrate users
                </TableCell>
                <TableCell data-col={2}>
                  {String(sysInfo.oauth_auto_migrate_to_cognito)}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Server url</TableCell>
                <TableCell data-col={2}>{sysInfo.server_url}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Last time scalars loaded</TableCell>
                <TableCell data-col={2}>
                  {sysInfo.last_time_scalars_loaded}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Last time scalars processed</TableCell>
                <TableCell data-col={2}>
                  {sysInfo.last_time_scalars_processed}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </SettingsSection>

      <SettingsSection title="Platform">
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-col={1}>Name</TableCell>
                <TableCell data-col={2}>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              data-qa_loading={sysInfoLoading}
              data-qa_load_count={tableLoadCount}
            >
              <TableRow>
                <TableCell data-col={1}>Architecture</TableCell>
                <TableCell data-col={2}>
                  {sysInfo.platform.architecture.join(", ")}
                </TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Machine</TableCell>
                <TableCell data-col={2}>{sysInfo.platform.machine}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Host</TableCell>
                <TableCell data-col={2}>{sysInfo.platform.host}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Version</TableCell>
                <TableCell data-col={2}>{sysInfo.platform.version}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Processor</TableCell>
                <TableCell data-col={2}>{sysInfo.platform.processor}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Platform</TableCell>
                <TableCell data-col={2}>{sysInfo.platform.platform}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </SettingsSection>

      <SettingsSection title="Database">
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-col={1}>Name</TableCell>
                <TableCell data-col={2}>Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              data-qa_loading={sysInfoLoading}
              data-qa_load_count={tableLoadCount}
            >
              <TableRow>
                <TableCell data-col={1}>Engine</TableCell>
                <TableCell data-col={2}>{sysInfo.database.engine}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Name</TableCell>
                <TableCell data-col={2}>{sysInfo.database.name}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>User</TableCell>
                <TableCell data-col={2}>{sysInfo.database.user}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Host</TableCell>
                <TableCell data-col={2}>{sysInfo.database.host}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell data-col={1}>Port</TableCell>
                <TableCell data-col={2}>{sysInfo.database.port}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </SettingsSection>

      <SettingsSection title="Last logins">
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-col={1}>Id</TableCell>
                <TableCell data-col={2}>Name</TableCell>
                <TableCell data-col={3}>Email</TableCell>
                <TableCell data-col={4}>Last login</TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              data-qa_loading={sysInfoLoading}
              data-qa_load_count={tableLoadCount}
            >
              {sysInfo.last_logins.map(u => {
                return (
                  <TableRow key={u.id}>
                    <TableCell data-col={1}>{u.id}</TableCell>
                    <TableCell data-col={2}>{u.name}</TableCell>
                    <TableCell data-col={3}>{u.email}</TableCell>
                    <TableCell data-col={4}>{u.last_login}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </SettingsSection>
      <EmailScheduleRunTable />
    </>
  );
};
